import { Grading } from "@mui/icons-material";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import CloseIcon from "@mui/icons-material/Close";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import DashboardIcon from "@mui/icons-material/Dashboard";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import EventIcon from "@mui/icons-material/Event";
import MapIcon from "@mui/icons-material/Map";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PeopleIcon from "@mui/icons-material/People";
import PermMediaIcon from "@mui/icons-material/PermMedia";
import PersonIcon from "@mui/icons-material/Person";
import ShareLocationIcon from "@mui/icons-material/ShareLocation";
import SummarizeIcon from "@mui/icons-material/Summarize";
import TrafficIcon from "@mui/icons-material/Traffic";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { IconButton } from "@mui/material";
import { usePopup } from "@nodeon/react-components";
import AccountCogIcon from "mdi-react/AccountCogIcon";
import AnchorIcon from "mdi-react/AnchorIcon";
import ApplicationCogIcon from "mdi-react/ApplicationCogIcon";
import ArchiveIcon from "mdi-react/ArchiveIcon";
import BriefcaseEditIcon from "mdi-react/BriefcaseEditIcon";
import FerryIcon from "mdi-react/FerryIcon";
import LightHouseIcon from "mdi-react/LighthouseIcon";
import WallIcon from "mdi-react/WallIcon";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Redirect, Route, Switch } from "react-router-dom";
import { AccessEnum } from "./api/GeneratedApi";
import DraggableDialog from "./components/DraggableDialog";
import FileExplorer from "./components/file-explorer/FileExplorer";
import { CameraViewPage } from "./pages/ais-vessel-page/components/CameraViewPage";
import { FairwayPage } from "./pages/fairway-maintenance-reporting-page/FairwayPage";
import LoggedOutPage from "./pages/LoggedOut/LoggedOutPage";
import OrganizationRolePage from "./pages/Management/organization/pages/OrganizationRolePage";
import UserEditPage from "./pages/Management/organization/pages/UserEditPage";
import OrganizationPage from "./pages/Management/OrganizationPage";
import MapAreaPage from "./pages/MapArea/MapAreaPage";
import { ProjectPage } from "./pages/ProjectsPage/ProjectPage";
import { ProjectsPage } from "./pages/ProjectsPage/ProjectsPage";
import LoadingIndicator from "./template/components/LoadingIndicator";
import {
  MainMenuCategory,
  MainMenuLink,
} from "./template/components/navigation/Navigator";
import SplashPage from "./template/pages/SplashPage/SplashPage";
import AppRoute from "./utils/AppRoute";
import useAuth from "./utils/hooks/useAuth";
import { useOnlyTLReadAccess } from "./utils/hooks/useOnlyTLReadAccess";
import { useTrafficBuoyRedirect } from "./utils/hooks/useTrafficBuoyRedirect";
import { useVideoJsAccessToken } from "./utils/hooks/useVideoJsAccessToken";

const PortsPage = React.lazy(() => import("./pages/PortsPage/PortsPage"));
const EventsPage = React.lazy(() => import("./pages/EventsPage/EventsPage"));

const ManagementPage = React.lazy(
  () => import("./pages/Management/ManagementPage"),
);
const TestPage =
  process.env.NODE_ENV !== "production"
    ? React.lazy(() => import("./pages/TestPage/TestPage"))
    : undefined;

// const MainPage = React.lazy(() => import("./pages/Main"));
const MapPage = React.lazy(() => import("./pages/MapPage/MapPage"));
const SettingsPage = React.lazy(() => import("./pages/Settings/SettingsPage"));
const FleetsPage = React.lazy(() => import("./pages/fleets-page/FleetsPage"));
const PortAndFairwayDesignPage = React.lazy(
  () =>
    import("./pages/marine-survey-and-design-page/MarineSurveyAndDesignPage"),
);

const DamPage = React.lazy(() => import("./pages/dam-page/DamPage"));
const FerryPage = React.lazy(() => import("./pages/ferry-page/FerryPage"));
const AtoNsList = React.lazy(() => import("./pages/aton-units-page/AtoNsList"));
const AtoNPage = React.lazy(() => import("./pages/aton-units-page/AtoNPage"));
const TrafficBuoyPage = React.lazy(
  () => import("./pages/traffic-buoys/TrafficBuoyPage"),
);
const TrackingDevicesPage = React.lazy(
  () => import("./pages/tracking-devices-page/TrackingDevicesPage"),
);
const EquipmentManagementPage = React.lazy(
  () => import("./pages/equipment-management-page/EquipmentManagementPage"),
);
const AISVesselsPage = React.lazy(
  () => import("./pages/ais-vessel-page/AISVesselPage"),
);

const testPage: MainMenuLink = {
  id: "test",
  icon: <ContactMailIcon />,
  to: "/test",
  translation: "testpage",
};

export const useRoutes = () => {
  const { show } = usePopup();
  const { t } = useTranslation("");
  const { user } = useAuth();

  const MainMenuItems: MainMenuCategory[] = useMemo(
    () => [
      {
        id: "navigation",
        children: [
          // {
          //   id: "home",
          //   icon: <HomeIcon />,
          //   to: "/",
          //   translation: "homepage",
          // },
          {
            id: "map",
            icon: <MapIcon />,
            to: "/map",
            translation: "mappage",
          },
          {
            id: "fleets",
            icon: <DashboardIcon />,
            to: "/fleets",
            translation: "fleets",
            requiredAccess: AccessEnum.FleetRead,
          },
          {
            id: "equipment-management",
            icon: <DirectionsCarIcon />,
            to: "/equipment-management/cars",
            translation: "equipment-management",
            requiredAccess: AccessEnum.EquipmentManagementRead,
            isActive: () =>
              window.location.href.includes("/equipment-management/"),
          },
          {
            id: "tracking-devices",
            icon: <ShareLocationIcon />,
            to: "/tracking-devices/yepzon",
            translation: "tracking-devices",
            requiredAccess: AccessEnum.RemoteTrackingDevicesRead,
            isActive: () => window.location.href.includes("/tracking-devices/"),
          },
          {
            id: "ais-vessels",
            icon: <FerryIcon />,
            to: "/ais-vessels",
            translation: "ais-vessels",
            isActive: () => window.location.href.includes("/ais-vessels"),
          },
          {
            id: "dams",
            icon: <WallIcon />,
            to: "/dams",
            translation: "dams",
            isActive: () => window.location.href.includes("/dams/"),
            requiredAccess: AccessEnum.DamRead,
          },
          {
            id: "ferries",
            icon: <FerryIcon />,
            to: "/ferries",
            translation: "ferries",
            requiredAccess: AccessEnum.FerryRead,
          },
          {
            id: "aton-units",
            icon: <LightHouseIcon />,
            to: "/atons",
            translation: "atons",
            isActive: () => window.location.href.includes("/atons"),
            requiredAccess: undefined,
          },
          {
            id: "traffic-buoys",
            icon: <TrafficIcon />,
            to: "/traffic-buoys",
            translation: "traffic-buoys",
            isActive: () => window.location.href.includes("/traffic-buoys"),
            requiredAccess: AccessEnum.TrafficLightBuoyRead,
          },
          ...(process.env.NODE_ENV !== "production" ? [testPage] : []),
          {
            id: "management",
            icon: <BriefcaseEditIcon />,
            translation: "managementpage",
            requiredAccess: AccessEnum.CreateUsers,
            children: [
              {
                id: "users",
                icon: <PeopleIcon />,
                to: "/users",
                translation: "users",
                isActive: () => window.location.href.includes("/users/"),
              },
              {
                id: "organization",
                icon: <BusinessCenterIcon />,
                to: "/organizations",
                translation: "organizations",
                isActive: () =>
                  window.location.href.includes("/organizations/"),
                hidden: !user?.isAdmin,
              },
              {
                id: "api-keys",
                icon: <VpnKeyIcon />,
                to: "/api-keys",
                translation: "apiKeys",
                isActive: () => window.location.href.includes("/api-keys/"),
                hidden: !user?.isAdmin,
              },
            ],
          },
          {
            id: "map-areas",
            icon: <ArchiveIcon />,
            to: "/map-areas",
            translation: "map-areas",
            requiredAccess: AccessEnum.MapAreaRead,
          },
          {
            id: "ports",
            icon: <AnchorIcon />,
            to: "/ports",
            translation: "ports",
            isActive: () => window.location.href.includes("/ports"),
            requiredAccess: AccessEnum.PortRead,
          },
          {
            id: "events",
            icon: <EventIcon />,
            to: "/events",
            isActive: () => window.location.href.includes("/events"),
            translation: "events",
            requiredAccess: AccessEnum.EventRead,
          },
          {
            id: "marine-survey-and-design",
            icon: <DesignServicesIcon />,
            to: "/marine-survey-and-design",
            isActive: () =>
              window.location.href.includes("/marine-survey-and-design"),
            translation: "marine-survey-and-design",
            requiredAccess: [
              AccessEnum.SeaSurveyRead,
              AccessEnum.DesignMaterialRead,
              AccessEnum.Map3DObjectRead,
            ],
          },
          // {
          //   id: "map-3d-objects",
          //   icon: <ExploreIcon />,
          //   to: "/3d-objects",
          //   translation: "map-3d-objects",
          // },
          {
            id: "file-explorer",
            icon: <PermMediaIcon />,
            translation: "file-explorer",
            requiredAccess: AccessEnum.OrganizationFileRead,
            onClick: () =>
              show(
                ({ close }) => (
                  <DraggableDialog
                    open
                    title={t("file-explorer:file-explorer")}
                    contentProps={{
                      sx: { padding: 0, height: "60vh" },
                    }}
                    toolbar={
                      <IconButton
                        style={{ marginLeft: "auto" }}
                        onClick={close}
                        size="large"
                      >
                        <CloseIcon />
                      </IconButton>
                    }
                  >
                    <FileExplorer embedded />
                  </DraggableDialog>
                ),
                { noDialog: true, id: "file-explorer" },
              ),
          },
          {
            id: "projects",
            icon: <Grading />,
            to: "/projects",
            isActive: () => window.location.href.includes("/projects"),
            translation: "projects",
            requiredAccess: AccessEnum.ProjectRead,
          },
          {
            id: "fairway-maintenance-reporting",
            icon: <SummarizeIcon />,
            to: "/fairway-maintenance-reporting",
            isActive: () =>
              window.location.href.includes("/fairway-maintenance-reporting"),
            translation: "fairway-maintenance-reporting",
            requiredAccess: AccessEnum.FairwayRead,
          },
          {
            id: "settings",
            icon: <AccountCogIcon />,
            translation: "settingspage",
            children: [
              {
                id: "profile",
                icon: <PersonIcon />,
                to: "/settings/profile",
                translation: "profilesettings",
              },
              {
                id: "site",
                icon: <ApplicationCogIcon />,
                to: "/settings/site",
                translation: "sitesettings",
              },
              {
                id: "Notifications",
                icon: <NotificationsIcon />,
                to: "/settings/Notifications",
                translation: "notifications",
              },
            ],
          },
        ],
        translation: "navigationblock",
      },
    ],
    [show, t, user?.isAdmin],
  );

  // const test = MainMenuItems[0].children
  //   .map((i) => {
  //     const items = i.children ? i.children.map((v) => v.id) : i.id;
  //     return items;
  //   })
  //   .flat();
  // // .flat();
  // console.log(test);

  return { MainMenuItems };
};

export const Routes = (): JSX.Element => {
  const { user } = useAuth();
  useVideoJsAccessToken();

  // TL Buoy user (read only) forced redirect
  const onlyTLReadAccess = useOnlyTLReadAccess();
  useTrafficBuoyRedirect();

  const TLRoute = (
    <AppRoute
      titleKey="traffic-buoys"
      path="/traffic-buoys/:tab?"
      exact
      requiredAccess={AccessEnum.TrafficLightBuoyRead}
    >
      <TrafficBuoyPage />
    </AppRoute>
  );
  if (onlyTLReadAccess)
    return (
      <React.Suspense fallback={<LoadingIndicator />}>
        <Switch>{TLRoute}</Switch>
      </React.Suspense>
    );

  return (
    <React.Suspense fallback={<LoadingIndicator />}>
      <Switch>
        {/* <AppRoute titleKey="homepage" path="/" exact>
        <MainPage />
      </AppRoute> */}
        <AppRoute
          titleKey="fleets"
          path="/fleets"
          exact
          requiredAccess={AccessEnum.FleetRead}
        >
          <FleetsPage />
        </AppRoute>
        <AppRoute titleKey="mappage" path="/map" exact>
          <MapPage />
        </AppRoute>
        {TestPage && (
          <AppRoute titleKey="Test" path="/test" exact>
            <TestPage />
          </AppRoute>
        )}
        <AppRoute
          titleKey="managementpage"
          path="/management"
          exact
          requiredAccess={AccessEnum.CreateUsers}
        >
          <ManagementPage tab="users" />
        </AppRoute>
        <AppRoute
          titleKey="users"
          path="/users"
          exact
          requiredAccess={AccessEnum.CreateUsers}
        >
          <ManagementPage tab="users" />
        </AppRoute>
        <AppRoute
          titleKey="organizations"
          path="/organizations"
          disabled={!user || !user.isAdmin}
          exact
        >
          <ManagementPage tab="organizations" />
        </AppRoute>
        <AppRoute
          titleKey="apiKeys"
          path="/api-keys"
          disabled={!user || !user.isAdmin}
          exact
        >
          <ManagementPage tab="api-keys" />
        </AppRoute>
        <AppRoute
          titleKey="organization-tab"
          path="/organizations/:id/:tab"
          disabled={!user || !user.isAdmin}
          hasBreadcrumbs
          exact
        >
          <OrganizationPage />
        </AppRoute>
        <AppRoute
          titleKey="user"
          path="/users/:id"
          exact
          hasBreadcrumbs
          requiredAccess={AccessEnum.CreateUsers}
        >
          <UserEditPage />
        </AppRoute>
        <AppRoute
          titleKey="organization"
          path="/organizations/:orgId/role/:orgRoleId"
          disabled={!user || !user.isAdmin}
          hasBreadcrumbs
          exact
        >
          <OrganizationRolePage />
        </AppRoute>
        <AppRoute
          titleKey="map-areas"
          path="/map-areas"
          exact
          requiredAccess={AccessEnum.MapAreaRead}
        >
          <MapAreaPage />
        </AppRoute>
        <AppRoute
          titleKey="ports"
          path="/ports/:tab?"
          exact
          requiredAccess={AccessEnum.PortRead}
        >
          <PortsPage />
        </AppRoute>
        <AppRoute
          titleKey="events"
          path="/events/:tab?"
          exact
          requiredAccess={AccessEnum.EventRead}
        >
          <EventsPage />
        </AppRoute>
        <AppRoute
          titleKey="dams"
          path="/dams/:tab?"
          exact
          requiredAccess={AccessEnum.DamRead}
        >
          <DamPage />
        </AppRoute>
        <AppRoute
          titleKey="ferries"
          path="/ferries"
          exact
          requiredAccess={AccessEnum.FerryRead}
        >
          <FerryPage />
        </AppRoute>
        <AppRoute titleKey="settingspage" path="/settings" exact>
          <SettingsPage tab="profile" />
        </AppRoute>
        <AppRoute titleKey="profilesettings" path="/settings/profile" exact>
          <SettingsPage tab="profile" />
        </AppRoute>
        <AppRoute titleKey="sitesettings" path="/settings/site" exact>
          <SettingsPage tab="site" />
        </AppRoute>
        <AppRoute titleKey="notifications" path="/settings/notifications" exact>
          <SettingsPage tab="Notifications" />
        </AppRoute>
        <AppRoute
          titleKey="marine-survey-and-design"
          path="/marine-survey-and-design/:tab?"
          exact
          requiredAccess={[
            AccessEnum.SeaSurveyRead,
            AccessEnum.DesignMaterialRead,
            AccessEnum.Map3DObjectRead,
          ]}
        >
          <PortAndFairwayDesignPage />
        </AppRoute>
        {/* <AppRoute titleKey="map-3d-objects" path="/3d-objects" exact>
        <Map3DObjectsPage />
      </AppRoute> */}
        <AppRoute
          titleKey="atons"
          path="/atons"
          exact
          requiredAccess={AccessEnum.SafetyDeviceRead}
        >
          <AtoNsList />
        </AppRoute>
        {TLRoute}
        <AppRoute
          titleKey="tracking-devices"
          path="/tracking-devices/:tab"
          exact
        >
          <TrackingDevicesPage />
        </AppRoute>
        <AppRoute titleKey="ais-vessels" path="/ais-vessels/:tab?" exact>
          <AISVesselsPage />
        </AppRoute>
        <AppRoute
          titleKey="cameras"
          hasBreadcrumbs
          path="/ais-vessels/cameras/:id/:action/:tab"
          exact
        >
          <CameraViewPage />
        </AppRoute>
        <AppRoute
          titleKey="equipment-management"
          path="/equipment-management/:tab"
          exact
        >
          <EquipmentManagementPage />
        </AppRoute>
        <AppRoute
          titleKey="aton-page"
          hasBreadcrumbs
          path="/atons/:id/:tab"
          exact
        >
          <AtoNPage />
        </AppRoute>
        <AppRoute
          titleKey="fairway-maintenance-reporting"
          path="/fairway-maintenance-reporting/:tab?"
          exact
          requiredAccess={AccessEnum.FairwayRead}
        >
          <FairwayPage />
        </AppRoute>
        <AppRoute titleKey="project-page" hasBreadcrumbs path="/projects" exact>
          <ProjectsPage />
        </AppRoute>
        <AppRoute
          titleKey="project-page"
          hasBreadcrumbs
          path="/projects/:id/:tab/"
          exact
          requiredAccess={AccessEnum.ProjectRead}
        >
          <ProjectPage />
        </AppRoute>
        <Route path="*">
          <Redirect to="/map" />
        </Route>
      </Switch>
    </React.Suspense>
  );
};

export const publicRoutes = (
  <Switch>
    <Route path="/loggedout" exact>
      <LoggedOutPage />
    </Route>
    <Route path="*">
      <SplashPage />
    </Route>
  </Switch>
);
